import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Typography, Progress, theme, App, Card } from 'antd';
import {
  MailOutlined,
  LockOutlined,
  SafetyOutlined,
  ArrowLeftOutlined,
  CheckCircleFilled,
  CloseCircleOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getBackendUrl } from "../../utils/env";

const { Title, Text } = Typography;

export default function ForgotPassword() {
  const [step, setStep] = useState(1); // 1: Email, 2: Código + Nueva Pass
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const { token } = theme.useToken();
  const navigate = useNavigate();
  const { message } = App.useApp();

  // Detección de tema
  const [isLight, setIsLight] = useState(document.body.getAttribute('data-theme') === 'light');

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsLight(document.body.getAttribute('data-theme') === 'light');
    });
    observer.observe(document.body, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, []);

  // Lógica de fortaleza de contraseña
  const getPasswordStrength = (pwd) => {
    let strength = 0;
    if (pwd.length >= 8) strength += 25;
    if (/[A-Za-z]/.test(pwd)) strength += 25;
    if (/[0-9]/.test(pwd)) strength += 25;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(pwd)) strength += 25;
    return strength;
  };

  const strength = getPasswordStrength(password);
  const strengthColor = strength < 50 ? '#ff4d4f' : strength < 100 ? '#faad14' : '#52c41a';

  // PASO 1: Solicitar código
  const handleSendCode = async (values) => {
    // 🛡️ Bloqueo preventivo de la cuenta de administrador
  if (values.email.toLowerCase() === "admin@example.com") {
    message.error("Este usuario no tiene permitido el restablecimiento por correo. Contacte al soporte técnico.");
    return;
  }

    setLoading(true);
    try {
      await axios.post(`${getBackendUrl()}api/auth/request_reset.php`, values);
      setEmail(values.email);
      setStep(2);
      message.success("Código enviado a tu correo");
    } catch (err) {
      message.error(err.response?.data?.message || "Error al enviar el código");
    } finally {
      setLoading(false);
    }
  };

  // PASO 2: Validar y cambiar pass
  const handleResetPassword = async (values) => {
    setLoading(true);
    try {
      await axios.post(`${getBackendUrl()}api/auth/reset_password.php`, {
        email,
        code: values.code,
        new_password: values.new_password
      });
      message.success("Contraseña actualizada con éxito");
      navigate('/');
    } catch (err) {
      message.error(err.response?.data?.message || "Código inválido o error de red");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: "100vh",
      background: isLight
        ? "linear-gradient(135deg, #fffbe6 0%, #ffffff 100%)"
        : "linear-gradient(135deg, #141414 0%, #1f1f1f 100%)",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      padding: "20px",
    }}>
      <div style={{
        background: token.colorBgContainer,
        padding: "clamp(24px, 6vw, 40px)",
        borderRadius: 24,
        width: "100%",
        maxWidth: 420,
        boxShadow: isLight ? "0 20px 60px rgba(0,0,0,0.05)" : "0 20px 60px rgba(0,0,0,0.3)",
        border: `1px solid ${token.colorBorderSecondary}`,
      }}>

        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => step === 1 ? navigate('/') : setStep(1)}
          style={{ marginBottom: 16, color: token.colorPrimary }}
        >
          {step === 1 ? "Volver al Login" : "Atrás"}
        </Button>

        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <Title level={2} style={{ margin: 0 }}>
            {step === 1 ? "Recuperar Acceso" : "Nueva Contraseña"}
          </Title>
          <Text type="secondary">
            {step === 1
              ? "Te enviaremos un código de seguridad."
              : `Ingresa el código enviado a ${email}`}
          </Text>
        </div>

        {step === 1 ? (
          <Form layout="vertical" onFinish={handleSendCode} requiredMark={false}>
            <Form.Item
              name="email"
              rules={[{ required: true, type: 'email', message: 'Ingresa un correo válido' }]}
            >
              <Input
                prefix={<MailOutlined style={{ color: token.colorTextDisabled }} />}
                placeholder="tu@correo.com"
                size="large"
                style={{ borderRadius: 12, height: 50, background: token.colorFillTertiary }}
              />
            </Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              block
              size="large"
              loading={loading}
              style={{
                height: 50,
                borderRadius: 12,
                fontWeight: 700,
                background: "linear-gradient(90deg, #fadb14, #faad14)",
                color: "#000",
                border: "none"
              }}
            >
              Enviar Código de Verificación
            </Button>
          </Form>
        ) : (
          <Form layout="vertical" onFinish={handleResetPassword} scrollToFirstError requiredMark={false}>

            <Form.Item
              name="code"
              label={<Text strong>Código de 6 dígitos</Text>}
              rules={[{ required: true, message: 'Ingresa el código enviado' }]}
            >
              <Input
                prefix={<SafetyOutlined />}
                placeholder="000000"
                size="large"
                maxLength={6}
                style={{ borderRadius: 12, height: 50, letterSpacing: 4, textAlign: 'center', fontWeight: 'bold' }}
              />
            </Form.Item>

            <Form.Item
              name="new_password"
              label={<Text strong>Nueva Contraseña</Text>}
              rules={[
                { required: true, message: 'La contraseña es obligatoria' },
                {
                  pattern: /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]{8,}$/,
                  message: 'No cumple requisitos mínimos'
                }
              ]}
              hasFeedback
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="Mínimo 8 caracteres"
                size="large"
                style={{ borderRadius: 12, height: 50 }}
                onChange={(e) => setPassword(e.target.value)}
              />
            </Form.Item>

            {/* BARRA DE FORTALEZA */}
            <div style={{ marginBottom: 20 }}>
              <Progress percent={strength} showInfo={false} strokeColor={strengthColor} strokeWidth={6} />
              <div style={{ marginTop: 12, padding: '12px', background: token.colorFillTertiary, borderRadius: 12 }}>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, fontSize: '12px' }}>
                  <li style={{ color: password.length >= 8 ? '#52c41a' : token.colorTextDescription }}>
                    {password.length >= 8 ? <CheckCircleFilled /> : <CloseCircleOutlined />} 8+ caracteres
                  </li>
                  <li style={{ color: /\d/.test(password) ? '#52c41a' : token.colorTextDescription }}>
                    {/\d/.test(password) ? <CheckCircleFilled /> : <CloseCircleOutlined />} Al menos un número
                  </li>
                  <li style={{ color: /[!@#$%^&*(),.?":{}|<>]/.test(password) ? '#52c41a' : token.colorTextDescription }}>
                    {/[!@#$%^&*(),.?":{}|<>]/.test(password) ? <CheckCircleFilled /> : <CloseCircleOutlined />} Un símbolo especial
                  </li>
                </ul>
              </div>
            </div>

            <Form.Item
              name="confirm_password"
              label={<Text strong>Confirmar Contraseña</Text>}
              dependencies={['new_password']}
              hasFeedback
              rules={[
                { required: true, message: 'Confirma la contraseña' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('new_password') === value) return Promise.resolve();
                    return Promise.reject(new Error('Las contraseñas no coinciden'));
                  },
                }),
              ]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="Repite la contraseña"
                size="large"
                style={{ borderRadius: 12, height: 50 }}
              />
            </Form.Item>

            <Button
              type="primary"
              htmlType="submit"
              block
              size="large"
              loading={loading}
              style={{
                height: 50,
                borderRadius: 12,
                fontWeight: 700,
                background: "linear-gradient(90deg, #fadb14, #faad14)",
                color: "#000",
                border: "none",
                boxShadow: "0 4px 15px rgba(250, 173, 20, 0.3)"
              }}
            >
              Actualizar Contraseña
            </Button>
          </Form>
        )}
      </div>
    </div>
  );
}